import React, { useState } from 'react';
import { User, Lock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LoginPage = ({ setIsLoggedIn }) => {
  const [formData, setFormData] = useState({
    UserName: '',
    Password: ''
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.UserName) {
      newErrors.UserName = 'Username is required';
    }
    if (!formData.Password) {
      newErrors.Password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (validateForm()) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/Login/CheckCredentials`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          setErrors({
            submitError: errorData.message || 'Login failed'
          });
          return;
        }

        const data = await response.json();

        // Store user data in sessionStorage with proper boolean conversion
        if (data.data) {
          sessionStorage.setItem('BUId', data.data.buId);
          // Convert boolean to string 'true'/'false' explicitly
          sessionStorage.setItem('isAdmin', String(data.data.isAdmin));
          sessionStorage.setItem('userName', data.data.userName || '');
          sessionStorage.setItem('isLoggedIn', 'true');
          
          // Log the stored values for debugging
          console.log('Stored isAdmin:', sessionStorage.getItem('isAdmin'));
          console.log('User data:', data.data);
          
          setIsLoggedIn(true);
          
          // Determine redirect path
          const origin = location.state?.from?.pathname || '/dashboard';
          navigate(origin);
        } else {
          throw new Error('Invalid response data structure');
        }
      } catch (error) {
        console.error('Login error:', error);
        setErrors({
          submitError: 'Network error. Please try again.'
        });
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        <h2 className="text-2xl font-bold mb-6 text-center">Login</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="UserName" className="block text-gray-700 mb-2">Username</label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                id="UserName"
                name="UserName"
                value={formData.UserName}
                onChange={handleChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${errors.UserName ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter Username"
              />
              {errors.UserName && (
                <p className="text-red-500 text-sm mt-1">{errors.UserName}</p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="Password" className="block text-gray-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="password"
                id="Password"
                name="Password"
                value={formData.Password}
                onChange={handleChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${errors.Password ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your Password"
              />
              {errors.Password && (
                <p className="text-red-500 text-sm mt-1">{errors.Password}</p>
              )}
            </div>
          </div>

          {errors.submitError && (
            <div className="mb-4 text-center">
              <p className="text-red-500 text-sm">{errors.submitError}</p>
            </div>
          )}

          <button 
            type="submit" 
            className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Login
          </button>
        </form>

      </div>
    </div>
  );
};

export default LoginPage;